import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { CheckCircle2, Copy, ExternalLink, BarChart3, Users, Eye, Loader2, Layout } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [stats, setStats] = useState({ visitor_count: 0, lead_count: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, [pageId]);

  const fetchData = async () => {
    try {
      const [pageRes, statsRes] = await Promise.all([
        axios.get(`${API}/pages/${pageId}`),
        axios.get(`${API}/pages/${pageId}/stats`)
      ]);
      setPage(pageRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getPublicUrl = () => {
    if (page?.subdomain) {
      return `https://${page.subdomain}.insta-landing-8.preview.emergentagent.com`;
    }
    return `${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/page/${pageId}`;
  };

  const handleCopyLink = async () => {
    const url = getPublicUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link kopiert!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="px-4 md:px-8 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Logo size="lg" clickable={false} />
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 md:py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8 animate-fade-in">
            <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center float-animation">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="mb-10 animate-slide-up">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Deine Seite ist live! 🎉
            </h1>
            <p className="text-lg text-slate-600">
              {page?.company_name} ist jetzt online und bereit für Besucher.
            </p>
          </div>

          {/* URL Card */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-slate-100 mb-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <p className="text-sm text-slate-500 mb-3">Deine öffentliche URL:</p>
            <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-xl">
              <code className="flex-1 text-indigo-600 font-medium text-sm md:text-base truncate">
                {getPublicUrl()}
              </code>
              <button
                data-testid="copy-url-btn"
                onClick={handleCopyLink}
                className={`p-2 rounded-lg transition-colors ${
                  copied ? "bg-green-100 text-green-600" : "bg-white hover:bg-slate-100 text-slate-600"
                }`}
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                data-testid="view-page-btn"
                onClick={() => window.open(getPublicUrl(), "_blank")}
                className="flex-1 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Seite ansehen
              </Button>
              <Button
                data-testid="view-leads-btn"
                variant="outline"
                onClick={() => navigate(`/dashboard/${pageId}`)}
                className="flex-1 h-12 rounded-full"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                Leads ansehen
              </Button>
            </div>
          </div>

          {/* Mini Dashboard */}
          <div className="grid grid-cols-2 gap-4 animate-fade-in" style={{ animationDelay: "0.4s" }}>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-50 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-slate-900 stat-number">{stats.visitor_count}</p>
              <p className="text-sm text-slate-500">Besucher</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-slate-900 stat-number">{stats.lead_count}</p>
              <p className="text-sm text-slate-500">Leads</p>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-12 p-6 bg-indigo-50 rounded-2xl text-left animate-fade-in" style={{ animationDelay: "0.6s" }}>
            <h3 className="font-semibold text-indigo-900 mb-3">💡 Nächste Schritte</h3>
            <ul className="space-y-2 text-sm text-indigo-700">
              <li>• Teile den Link auf Social Media und in deiner E-Mail-Signatur</li>
              <li>• Du erhältst eine E-Mail-Benachrichtigung bei jeder neuen Anfrage</li>
              <li>• Behalte deine Leads im Dashboard im Blick</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
